import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowLeftRight,
  Upload,
  LayoutDashboard,
  ShoppingCart,
  Package,
  Receipt,
  Users as UsersIcon,
  DollarSign,
  ShoppingBag,
  SlidersHorizontal,
  BarChart3,
  Shield,
  Settings,
  Wifi,
  X,
  LogOut,
  Menu,
  Brain,
  Plug,
  Calculator,
  ChevronDown,
  ChevronRight,
  FolderOpen,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import Button from '@/components/ui/Button';

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
}

interface NavigationCategory {
  name: string;
  icon: any;
  items: NavigationItem[];
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const navigationCategories: NavigationCategory[] = [
    {
      name: 'Business Operations',
      icon: ShoppingCart,
      items: [
        { name: 'POS', href: '/pos', icon: ShoppingCart },
        { name: 'Products', href: '/products', icon: Package },
        { name: 'Sales', href: '/sales', icon: Receipt },
        { name: 'Contacts', href: '/contacts', icon: UsersIcon },
        { name: 'Expenses', href: '/expenses', icon: DollarSign },
        { name: 'Purchases', href: '/purchases', icon: ShoppingBag },
      ],
    },
    {
      name: 'Inventory Management',
      icon: Package,
      items: [
        { name: 'Stock Adjustments', href: '/stock-adjustments', icon: SlidersHorizontal },
        { name: 'Stock Transfers', href: '/stock-transfers', icon: ArrowLeftRight },
        { name: 'Inventory Upload', href: '/inventory-upload', icon: Upload },
      ],
    },
    {
      name: 'BizCore Modules',
      icon: Brain,
      items: [
        { name: 'Business Profile', href: '/business-profile', icon: Building2 },
        { name: 'Smart Ledger', href: '/smart-ledger', icon: Calculator },
        { name: 'Expense & Proof', href: '/expense-proof', icon: Receipt },
        { name: 'Document Vault', href: '/document-vault', icon: FolderOpen },
      ],
    },
    {
      name: 'Financial Management',
      icon: Calculator,
      items: [
        { name: 'Accounting', href: '/accounting', icon: Calculator },
        { name: 'Reports', href: '/reports', icon: BarChart3 },
        { name: 'Business Intelligence', href: '/business-intelligence', icon: BarChart3 },
        { name: 'Branches', href: '/branches', icon: Building2 },
      ],
    },
    {
      name: 'Compliance & Audit',
      icon: Shield,
      items: [
        { name: 'Compliance', href: '/compliance', icon: Shield },
        { name: 'Audit', href: '/audit', icon: Shield },
        { name: 'Documents', href: '/documents', icon: Upload },
        { name: 'Offline Engine', href: '/offline-engine', icon: Wifi },
      ],
    },
  ];

  const standaloneItems: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Integration', href: '/integration', icon: Plug },
  ];

  const settingsItems: NavigationItem[] = [
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const toggleCategory = (categoryName: string) => {
    setCategoriesOpen(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white">
          <div className="flex items-center justify-between h-16 px-4 bg-blue-600">
            <h1 className="text-xl font-semibold text-white">BizCore</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-white hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {standaloneItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {item.icon && <item.icon className="mr-3 h-5 w-5" />}
                {item.name}
              </Link>
            ))}
            {navigationCategories.map((category) => (
              <div key={category.name}>
                <button
                  onClick={() => toggleCategory(category.name)}
                  className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
                >
                  <div className="flex items-center">
                    {category.icon && <category.icon className="mr-3 h-5 w-5" />}
                    {category.name}
                  </div>
                  {categoriesOpen[category.name] ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                {categoriesOpen[category.name] && (
                  <div className="mt-1 space-y-1">
                    {category.items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md pl-11 ${
                          isActive(item.href)
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        {item.icon && <item.icon className="mr-3 h-5 w-5" />}
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {settingsItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {item.icon && <item.icon className="mr-3 h-5 w-5" />}
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
            <div className="flex items-center h-16 px-4 bg-blue-600">
              <h1 className="text-xl font-semibold text-white">BizCore</h1>
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {standaloneItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {item.icon && <item.icon className="mr-3 h-5 w-5" />}
                  {item.name}
                </Link>
              ))}
              {navigationCategories.map((category) => (
                <div key={category.name}>
                  <button
                    onClick={() => toggleCategory(category.name)}
                    className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
                  >
                    <div className="flex items-center">
                      {category.icon && <category.icon className="mr-3 h-5 w-5" />}
                      {category.name}
                    </div>
                    {categoriesOpen[category.name] ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {categoriesOpen[category.name] && (
                    <div className="mt-1 space-y-1">
                      {category.items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md pl-11 ${
                            isActive(item.href)
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          {item.icon && <item.icon className="mr-3 h-5 w-5" />}
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {settingsItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {item.icon && <item.icon className="mr-3 h-5 w-5" />}
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 lg:pl-0">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-600"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.username?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <div className="text-sm font-medium text-gray-900">{user?.username || 'User'}</div>
                  <div className="text-xs text-gray-500">{user?.role || 'Staff'}</div>
                </div>
              </div>
              <Button variant="ghost" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-auto">
          <div className="py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
